import { treeNodeFilterFunction } from "@/utils/formUtils";
import { DataNode } from "@rc-component/tree-select/es/interface";
import { TreeSelect } from "antd";
import groupBy from "lodash/groupBy";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import { UUID } from "../../../../typings/global";
import { InstitutionType } from "../../../institution/enums";
import { EnumerationType } from "../../enums";

const groupFilter = (value: string) => value.startsWith("group-");

type Props = {
  value?: UUID[];
  onChange?: (value: UUID[]) => void;
  allowClear?: boolean;
};

export const InstitutionTreeSelect = ({ value, onChange, allowClear = true }: Props) => {
  const { t } = useTranslation();

  const allInstitutionsEnums = useEnumeration(EnumerationType.INSTITUTIONS);
  const institutionsEnums = useMemo(
    () => allInstitutionsEnums.filter((institution) => !institution.deactivated),
    [allInstitutionsEnums],
  );

  const institutionsTree = useMemo(() => {
    const institutionGroups = groupBy(institutionsEnums, (i) => i.type);

    return Object.keys(institutionGroups)
      .sort((a, b) => Object.keys(InstitutionType).indexOf(a) - Object.keys(InstitutionType).indexOf(b))
      .map<DataNode>((type) => ({
        value: `group-${type}`,
        title: t("institution.helpers.optGroupSector." + type),
        children: institutionGroups[type]?.map<DataNode>((institution) => ({
          value: institution.id,
          title: institution.name,
        })),
      }));
  }, [institutionsEnums]);

  return (
    <TreeSelect
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeCheckable
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      allowClear={allowClear}
      showSearch={{ filterTreeNode: treeNodeFilterFunction }}
      treeData={institutionsTree}
      onChange={(data) => {
        if (!data) {
          return;
        }

        const institutionValues = data.filter((value) => !groupFilter(value));

        const institutionGroupValues = data
          .filter(groupFilter)
          .map((value) => value.split("group-")[1])
          .filter((value): value is string => !!value);

        const result = [
          ...institutionValues,
          ...institutionsEnums
            .filter((institution) => institutionGroupValues.includes(institution.type))
            .map((institution) => institution.id),
        ];

        onChange?.(result);
      }}
      value={value}
    />
  );
};
